import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { TinySliderInfo, TinySliderProps } from "tiny-slider-react";
import "tiny-slider/src/tiny-slider.scss";
import useInViewport from "../../hooks/useInViewport";

export interface SliderControlProps {
  isFirst?: boolean;
  isLast?: boolean;
  onClickPrev?: VoidFunction;
  onClickNext?: VoidFunction;
}
export interface SliderProps extends TinySliderProps {
  children?: React.ReactNode;
  Controls?: React.FunctionComponent<SliderControlProps>;
}

const getPosition = (loop: boolean, info: TinySliderInfo) => {
  const hasMoreItems = info.slideCount > info.items;
  let isFirst = false;
  let isLast = false;

  if (!hasMoreItems) {
    isFirst = true;
    isLast = true;
  } else if (!loop) {
    if (info.index === 0) {
      isFirst = true;
    } else if (info.index + info.items === info.slideCount) {
      isLast = true;
    }
  }

  return {
    isFirst,
    isLast,
  };
};

const Slider = forwardRef<React.Ref<React.Component<SliderProps>>, SliderProps>(
  function Slider({ settings = {}, children, Controls, ...otherProps }, ref) {
    const sliderRef = useRef<any>();
    const [controlsProps, setControlsProps] =
      useState<SliderControlProps | null>(null);
    // Track if slider has been initialized to prevent DOM conflicts
    const [sliderInitialized, setSliderInitialized] = useState(false);

    // Defer initialization until component is near viewport (200px margin)
    const [viewportRef, isNearViewport] = useInViewport(0, true, "200px");

    // Once near viewport, mark as ready to initialize (one-way transition)
    useEffect(() => {
      if (isNearViewport && !sliderInitialized) {
        setSliderInitialized(true);
      }
    }, [isNearViewport, sliderInitialized]);

    useImperativeHandle(ref, () => sliderRef?.current?.slider || {}, [
      sliderRef,
      sliderInitialized,
    ]);

    // Cleanup slider before unmount to prevent "NotFoundError" when DOM is removed
    // during page navigation. useLayoutEffect runs synchronously before DOM removal.
    useLayoutEffect(() => {
      return () => {
        try {
          if (sliderRef?.current?.slider?.destroy) {
            sliderRef.current.slider.destroy();
          }
        } catch {
          // Silently ignore destroy errors during navigation
        }
      };
    }, []);

    useEffect(() => {
      if (!sliderRef?.current?.slider || !Controls) {
        return;
      }

      const isLoop = settings.loop === false ? false : true;
      const info = sliderRef.current.slider.getInfo();

      const controlPropsBase = {
        onClickPrev: () => {
          sliderRef.current?.slider?.goTo("prev");
        },
        onClickNext: () => {
          sliderRef.current?.slider?.goTo("next");
        },
      };

      setControlsProps({
        ...controlPropsBase,
        ...getPosition(isLoop, info),
      });

      const onIndexChanged = (newInfo: TinySliderInfo) => {
        setControlsProps({
          ...controlPropsBase,
          ...getPosition(isLoop, newInfo),
        });
      };

      sliderRef.current.slider.events.on("indexChanged", onIndexChanged);

      return () => {
        if (sliderRef?.current?.slider?.events) {
          sliderRef.current.slider.events.off("indexChanged", onIndexChanged);
        }
      };
    }, [sliderRef, setControlsProps, settings, Controls, sliderInitialized]);

    // SSR: render children directly
    if (typeof window === "undefined") {
      return <div>{children}</div>;
    }

    // Before slider is initialized, render placeholder for intersection observer
    if (!sliderInitialized) {
      return (
        <div ref={viewportRef}>
          {children}
        </div>
      );
    }

    // Once initialized, render the slider with a unique key to prevent React
    // from trying to reconcile children that tiny-slider has manipulated
    const Component = require("tiny-slider-react").default;

    return (
      <>
        <Component
          key="tns-slider"
          settings={settings}
          ref={sliderRef}
          {...otherProps}
        >
          {children}
        </Component>
        {!!controlsProps && Controls && <Controls {...controlsProps} />}
      </>
    );
  }
);

export default Slider;
